/**
 * LoadingPanel
 *
 * Inline (non-blocking) animated loader for long operations.
 * Keeps the layout usable (sidebar/navigation stays interactive).
 */

import type { ReactNode } from 'react';
import Lottie from 'lottie-react';

import loadingAnimation from '@/assets/animation/Man Robot Computers Animation.json';
import { Card } from '@/components/ui';
import { useMemeLoaderForUi } from '@/providers/MemeLoaderPreferenceProvider';
import { cn } from '@/lib/utils';

import { MemeMediaView, MEME_GIF_URLS, useRandomMemeMediaIndex } from './meme-loader';

interface LoadingPanelProps {
  title?: string;
  description?: string;
  /** Larger variant for prominent loading states (e.g. full-page tables) */
  size?: 'default' | 'lg';
  className?: string;
}

function defaultRobotVisual(isLg: boolean) {
  return (
    <div
      className={cn(
        'shrink-0',
        isLg ? 'h-36 w-36 md:h-44 md:w-44' : 'h-28 w-28 md:h-32 md:w-32',
      )}
    >
      <Lottie animationData={loadingAnimation} loop autoplay className="h-full w-full" aria-hidden="true" />
    </div>
  );
}

function panelCopy(title: string, description: string | undefined, isLg: boolean) {
  return (
    <div className={cn('space-y-1', isLg && 'space-y-2')}>
      <div className={cn('font-semibold text-foreground', isLg ? 'text-lg md:text-xl' : 'text-base')}>
        {title}
      </div>
      {description ? (
        <div className={cn('text-muted-foreground', isLg ? 'text-sm md:text-base' : 'text-sm')}>
          {description}
        </div>
      ) : null}
      <span className="sr-only">{title}</span>
    </div>
  );
}

function panelShell(isLg: boolean, className: string | undefined, visual: ReactNode, title: string, description?: string) {
  return (
    <Card
      className={cn('border-primary/20 bg-primary/5 p-4', isLg && 'p-8', className)}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center gap-3 text-center md:flex-row md:items-center md:text-left">
        {visual}
        {panelCopy(title, description, isLg)}
      </div>
    </Card>
  );
}

export function LoadingPanel({
  title = 'Loading…',
  description,
  size = 'default',
  className,
}: LoadingPanelProps) {
  const { memeLoaderEnabled } = useMemeLoaderForUi();
  const mediaIndex = useRandomMemeMediaIndex(MEME_GIF_URLS.length);
  const isLg = size === 'lg';

  const visual = memeLoaderEnabled ? (
    <div className="shrink-0">
      <MemeMediaView src={MEME_GIF_URLS[mediaIndex]} layout={isLg ? 'panel-lg' : 'panel'} />
    </div>
  ) : (
    defaultRobotVisual(isLg)
  );

  return panelShell(isLg, className, visual, title, description);
}
